import { useEffect } from 'react';
import { persistStore } from 'src/persist-store';

/** Inline until profile hooks migrate to SWR (Phase 4) */
interface CacheConfig {
  key: string;
  storage: 'session' | 'local' | 'secure';
  ttl?: number;
}

// ----------------------------------------------------------------------

export interface UsePersistStoreReturn {
  // Direct storage access
  getLocal: <T>(key: string) => Promise<T | null>;
  putLocal: <T>(key: string, value: T, persistOnLogout?: boolean) => Promise<void>;
  removeLocal: (key: string) => Promise<void>;
  
  getSession: <T>(key: string) => Promise<T | null>;
  putSession: <T>(key: string, value: T, persistOnLogout?: boolean) => Promise<void>;
  removeSession: (key: string) => Promise<void>;
  
  getSecure: <T>(key: string) => Promise<T | null>;
  putSecure: <T>(key: string, value: T, persistOnLogout?: boolean) => Promise<void>;
  removeSecure: (key: string) => Promise<void>;
  
  // Unified cache interface (for CommonContext)
  getFromCache: <T>(config: CacheConfig) => Promise<T | null>;
  setToCache: <T>(config: CacheConfig, data: T) => Promise<void>;
  removeFromCache: (config: CacheConfig) => Promise<void>;
  
  // Lifecycle management
  clearAllNonPersistent: () => Promise<void>;
}

// Initialize secure worker once at module level
let isSecureWorkerInitialized = false;

const initializeSecureWorker = () => {
  if (!isSecureWorkerInitialized) {
    persistStore.initSecureWorker();
    isSecureWorkerInitialized = true;
  }
};

/**
 * Custom hook for persistent storage operations
 */
export const usePersistStore = (): UsePersistStoreReturn => {
  // Initialize secure worker on first use
  useEffect(() => {
    initializeSecureWorker();
  }, []);

  // Direct storage methods
  const getLocal = async <T,>(key: string): Promise<T | null> => {
    return persistStore.getLocal<T>(key);
  };

  const putLocal = async <T,>(key: string, value: T, persistOnLogout = false): Promise<void> => {
    return persistStore.putLocal(key, value, { persistOnLogout });
  };

  const removeLocal = async (key: string): Promise<void> => {
    return persistStore.removeLocal(key);
  };

  const getSession = async <T,>(key: string): Promise<T | null> => {
    return persistStore.getSession<T>(key);
  };

  const putSession = async <T,>(key: string, value: T, persistOnLogout = false): Promise<void> => {
    return persistStore.putSession(key, value, { persistOnLogout });
  };

  const removeSession = async (key: string): Promise<void> => {
    return persistStore.removeSession(key);
  };

  const getSecure = async <T,>(key: string): Promise<T | null> => {
    return persistStore.getSecure<T>(key);
  };

  const putSecure = async <T,>(key: string, value: T, persistOnLogout = false): Promise<void> => {
    return persistStore.putSecure(key, value, { persistOnLogout });
  };

  const removeSecure = async (key: string): Promise<void> => {
    return persistStore.removeSecure(key);
  };

  // Unified cache interface
  const getFromCache = async <T,>(config: CacheConfig): Promise<T | null> => {
    try {
      switch (config.storage) {
        case 'session':
          return await persistStore.getSession<T>(config.key);
        case 'local':
          return await persistStore.getLocal<T>(config.key);
        case 'secure':
          return await persistStore.getSecure<T>(config.key);
        default:
          throw new Error(`Unsupported storage type: ${config.storage}`);
      }
    } catch (error) {
      console.error(`Failed to get ${config.key} from ${config.storage} storage:`, error);
      return null;
    }
  };

  const setToCache = async <T,>(config: CacheConfig, data: T): Promise<void> => {
    try {
      const dataWithTimestamp = config.ttl ? {
        data,
        timestamp: Date.now(),
        ttl: config.ttl
      } : data;

      switch (config.storage) {
        case 'session':
          await persistStore.putSession(config.key, dataWithTimestamp);
          break;
        case 'local':
          await persistStore.putLocal(config.key, dataWithTimestamp);
          break;
        case 'secure':
          await persistStore.putSecure(config.key, dataWithTimestamp);
          break;
        default:
          throw new Error(`Unsupported storage type: ${config.storage}`);
      }
    } catch (error) {
      console.error(`Failed to set ${config.key} to ${config.storage} storage:`, error);
    }
  };

  const removeFromCache = async (config: CacheConfig): Promise<void> => {
    try {
      switch (config.storage) {
        case 'session':
          await persistStore.removeSession(config.key);
          break;
        case 'local':
          await persistStore.removeLocal(config.key);
          break;
        case 'secure':
          await persistStore.removeSecure(config.key);
          break;
        default:
          throw new Error(`Unsupported storage type: ${config.storage}`);
      }
    } catch (error) {
      console.error(`Failed to remove ${config.key} from ${config.storage} storage:`, error);
    }
  };

  const clearAllNonPersistent = async (): Promise<void> => {
    try {
      await persistStore.clearOnLogout();
    } catch (error) {
      console.error('Failed to clear non-persistent data:', error);
    }
  };

  return {
    getLocal,
    putLocal,
    removeLocal,
    getSession,
    putSession,
    removeSession,
    getSecure,
    putSecure,
    removeSecure,
    getFromCache,
    setToCache,
    removeFromCache,
    clearAllNonPersistent,
  };
};
